import React from "react";
import {
  FiEdit2,
  FiExternalLink,
  FiGlobe,
  FiMail,
  FiMapPin,
  FiPhone,
  FiServer,
} from "react-icons/fi";
import { SlEarphonesAlt } from "react-icons/sl";

import AdminButton from "../../_shared/ui/AdminButton";
import AdminDrawer from "../../_shared/ui/AdminDrawer";
import {
  ActiveBadge,
  PublicBadge,
} from "./ServiceMunicipalStatusBadges";

import {
  getGoogleMapsUrl,
  getServiceAddress,
  getServiceTypeLabel,
  normalizeExternalUrl,
} from "../utils/servicesMunicipauxAdminUtils";

export default function ServicesMunicipauxPreviewDrawer({
  open,
  item,
  onClose,
  onEditFromPreview,
}) {
  const address = getServiceAddress(item);
  const mapsUrl = getGoogleMapsUrl(item?.gps_service);

  return (
    <AdminDrawer
      open={open}
      onClose={onClose}
      title="Aperçu du service municipal"
      subtitle="Visualisez la fiche telle qu’elle peut être exploitée côté site."
      icon={SlEarphonesAlt}
      size="xl"
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <AdminButton variant="secondary" onClick={onClose}>
            Fermer
          </AdminButton>

          {item?.id ? (
            <AdminButton
              icon={FiEdit2}
              variant="primary"
              onClick={onEditFromPreview}
            >
              Modifier ce service
            </AdminButton>
          ) : null}
        </div>
      }
    >
      {!item ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Service municipal introuvable.
        </p>
      ) : (
        <article className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="relative overflow-hidden bg-gradient-to-br from-cyan-700 via-sky-600 to-indigo-600 p-6 text-white sm:p-8">
            <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-white/15 blur-3xl" />

            <div className="relative">
              <div className="flex flex-wrap gap-2">
                <ActiveBadge active={!!item.is_active} />
                <PublicBadge isPublic={!!item.is_public} />
              </div>

              <div className="mt-6 flex items-start gap-4">
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-[1.5rem] border border-white/20 bg-white/15 text-3xl shadow-lg">
                  {item.icone_service ? (
                    <span className="text-sm font-bold">{item.icone_service}</span>
                  ) : (
                    <SlEarphonesAlt />
                  )}
                </div>

                <div>
                  <div className="flex flex-wrap gap-2">
                    {item.categorie_service ? (
                      <span className="rounded-full border border-white/20 bg-white/15 px-3 py-1 text-xs font-bold">
                        {item.categorie_service}
                      </span>
                    ) : null}

                    {item.type_service ? (
                      <span className="rounded-full border border-white/20 bg-white/15 px-3 py-1 text-xs font-bold">
                        {getServiceTypeLabel(item.type_service)}
                      </span>
                    ) : null}
                  </div>

                  <h1 className="mt-4 text-3xl font-bold leading-tight sm:text-4xl">
                    {item.nom_service}
                  </h1>

                  {item.accroche_service ? (
                    <p className="mt-3 max-w-3xl text-sm leading-7 text-white/85 sm:text-base">
                      {item.accroche_service}
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          <div className="grid gap-5 p-5 sm:p-7 lg:grid-cols-[1fr_340px]">
            <div className="space-y-6">
              {item.description_service ? (
                <section>
                  <h2 className="text-lg font-bold text-slate-950 dark:text-white">
                    Description
                  </h2>

                  <div
                    className="prose prose-slate mt-3 max-w-none dark:prose-invert prose-headings:font-bold prose-a:text-sky-600"
                    dangerouslySetInnerHTML={{
                      __html: item.description_service,
                    }}
                  />
                </section>
              ) : null}

              {item.horaires_service ? (
                <section className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
                  <h2 className="text-base font-bold text-slate-950 dark:text-white">
                    Horaires
                  </h2>

                  <p className="mt-3 whitespace-pre-line text-sm leading-6 text-slate-600 dark:text-slate-300">
                    {item.horaires_service}
                  </p>
                </section>
              ) : null}

              {item.infos_complementaires ? (
                <section className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
                  <h2 className="text-base font-bold text-slate-950 dark:text-white">
                    Informations complémentaires
                  </h2>

                  <p className="mt-3 whitespace-pre-line text-sm leading-6 text-slate-600 dark:text-slate-300">
                    {item.infos_complementaires}
                  </p>
                </section>
              ) : null}

              {!item.description_service &&
              !item.horaires_service &&
              !item.infos_complementaires ? (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Aucune information détaillée renseignée.
                </p>
              ) : null}
            </div>

            <aside className="space-y-4">
              <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
                <p className="text-sm font-bold text-slate-950 dark:text-white">
                  Informations
                </p>

                <div className="mt-3 space-y-2 text-sm text-slate-600 dark:text-slate-300">
                  {item.slug_service ? (
                    <p>
                      Slug :{" "}
                      <code className="rounded bg-slate-200 px-1.5 py-0.5 text-xs dark:bg-slate-800">
                        {item.slug_service}
                      </code>
                    </p>
                  ) : null}

                  <p>Ordre : {item.ordre_affichage ?? 0}</p>

                  {item.parent_id ? <p>Parent ID : {item.parent_id}</p> : null}

                  {item.icone_service ? <p>Icône : {item.icone_service}</p> : null}
                </div>
              </div>

              <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
                <p className="text-sm font-bold text-slate-950 dark:text-white">
                  Coordonnées
                </p>

                <div className="mt-3 space-y-2 text-sm text-slate-600 dark:text-slate-300">
                  {item.tel_service ? (
                    <p className="flex items-center gap-2">
                      <FiPhone />
                      {item.tel_service}
                    </p>
                  ) : null}

                  {item.tel_secondaire ? (
                    <p className="flex items-center gap-2">
                      <FiPhone />
                      {item.tel_secondaire}
                    </p>
                  ) : null}

                  {item.mail_service ? (
                    <p className="flex items-start gap-2">
                      <FiMail className="mt-1 shrink-0" />
                      <span>{item.mail_service}</span>
                    </p>
                  ) : null}

                  {address ? (
                    <p className="flex items-start gap-2 whitespace-pre-line">
                      <FiMapPin className="mt-1 shrink-0" />
                      {address}
                    </p>
                  ) : null}

                  {item.gps_service ? (
                    <p className="flex items-center gap-2">
                      <FiMapPin />
                      {item.gps_service}
                    </p>
                  ) : null}

                  {!item.tel_service &&
                  !item.tel_secondaire &&
                  !item.mail_service &&
                  !address &&
                  !item.gps_service ? (
                    <p>Aucune coordonnée renseignée.</p>
                  ) : null}
                </div>
              </div>

              <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
                <p className="text-sm font-bold text-slate-950 dark:text-white">
                  Liens utiles
                </p>

                <div className="mt-3 flex flex-col gap-2">
                  {item.website_service ? (
                    <a
                      href={normalizeExternalUrl(item.website_service)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 rounded-2xl bg-sky-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-sky-700"
                    >
                      <FiGlobe />
                      Site web
                    </a>
                  ) : null}

                  {item.autre_url_service ? (
                    <a
                      href={normalizeExternalUrl(item.autre_url_service)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 rounded-2xl bg-indigo-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-indigo-700"
                    >
                      <FiExternalLink />
                      Autre lien
                    </a>
                  ) : null}

                  {mapsUrl ? (
                    <a
                      href={mapsUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 rounded-2xl bg-amber-500 px-4 py-3 text-sm font-bold text-white transition hover:bg-amber-600"
                    >
                      <FiMapPin />
                      Ouvrir la carte
                    </a>
                  ) : null}

                  {!item.website_service && !item.autre_url_service && !mapsUrl ? (
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      Aucun lien renseigné.
                    </p>
                  ) : null}
                </div>
              </div>
            </aside>
          </div>
        </article>
      )}
    </AdminDrawer>
  );
}